import { useState, useEffect } from 'react';
import { CalendarDays } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
  Button, Label, Checkbox, Separator,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@higgins/ui';
import { useContextOptions } from '@/lib/hooks';
import { formatMonthYear } from '@/lib/types';
import type { NewReportContext } from '@/lib/types';
import { LAST_COMPLETED_MONTH, LAST_COMPLETED_YEAR, completedMonths } from '@/lib/period';

interface ContextSelectorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (context: NewReportContext) => void;
}

export function ContextSelector({ open, onOpenChange, onSubmit }: ContextSelectorProps) {
  const { data: options } = useContextOptions();

  const regions          = options?.regions          ?? [];
  const regionBranches   = options?.regionBranches   ?? {};
  const branchDivisions  = options?.branchDivisions  ?? {};

  const [region,    setRegion]    = useState<string>('');
  const [selectedBranches,  setSelectedBranches]  = useState<string[]>([]);
  const [selectedDivisions, setSelectedDivisions] = useState<string[]>([]);
  const [month, setMonth] = useState<number>(LAST_COMPLETED_MONTH);
  const [year,  setYear]  = useState<number>(LAST_COMPLETED_YEAR);
  const [populateFromPrevious, setPopulateFromPrevious] = useState(false);

  // Auto-select region when there's only one option
  useEffect(() => {
    if (regions.length === 1 && !region) setRegion(regions[0]);
  }, [regions, region]);

  // Auto-select all branches when region changes
  useEffect(() => {
    const branches = regionBranches[region] ?? [];
    setSelectedBranches(branches);
  }, [region]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select all divisions when branches change, keeping only valid ones
  useEffect(() => {
    const allDivisions = selectedBranches
      .flatMap(b => branchDivisions[b] ?? [])
      .filter((d, i, arr) => arr.indexOf(d) === i)
      .sort();
    setSelectedDivisions(allDivisions);
  }, [selectedBranches]); // eslint-disable-line react-hooks/exhaustive-deps

  const availableBranches  = regionBranches[region] ?? [];
  const availableDivisions = selectedBranches
    .flatMap(b => branchDivisions[b] ?? [])
    .filter((d, i, arr) => arr.indexOf(d) === i)
    .sort();

  function toggleBranch(branch: string) {
    setSelectedBranches(prev =>
      prev.includes(branch) ? prev.filter(b => b !== branch) : [...prev, branch]
    );
  }

  function toggleDivision(division: string) {
    setSelectedDivisions(prev =>
      prev.includes(division) ? prev.filter(d => d !== division) : [...prev, division]
    );
  }

  function handleSubmit() {
    onSubmit({
      region,
      branches: selectedBranches,
      divisions: selectedDivisions,
      month,
      year,
      populateFromPrevious,
    });
    reset();
  }

  function reset() {
    setRegion('');
    setSelectedBranches([]);
    setSelectedDivisions([]);
    setMonth(LAST_COMPLETED_MONTH);
    setYear(LAST_COMPLETED_YEAR);
    setPopulateFromPrevious(false);
  }

  const canSubmit = !!region && selectedBranches.length > 0 && selectedDivisions.length > 0;

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New Report</DialogTitle>
          <DialogDescription>
            Select the context for this monthly GM report.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-1">
          {/* Region */}
          <div className="space-y-1.5">
            <Label>Region</Label>
            {regions.length === 1 ? (
              <div className="h-10 flex items-center px-3 rounded-md bg-muted text-sm text-muted-foreground border border-input">
                {region}
              </div>
            ) : (
              <Select value={region} onValueChange={setRegion}>
                <SelectTrigger>
                  <SelectValue placeholder="Select region…" />
                </SelectTrigger>
                <SelectContent>
                  {regions.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>

          <Separator />

          {/* Report month — completed months only */}
          <div className="space-y-1.5">
            <Label>Report month</Label>
            <Select
              value={`${year}-${month}`}
              onValueChange={v => { const [y, m] = v.split('-').map(Number); setYear(y); setMonth(m); }}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {completedMonths().map(p => (
                  <SelectItem key={`${p.year}-${p.month}`} value={`${p.year}-${p.month}`}>
                    {formatMonthYear(p.month, p.year)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Separator />

          {/* Branches */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Branches</Label>
              {availableBranches.length > 0 && (
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <Checkbox
                    checked={selectedBranches.length === availableBranches.length}
                    onCheckedChange={v => setSelectedBranches(v ? [...availableBranches] : [])}
                  />
                  <span className="text-xs text-muted-foreground">Select all</span>
                </label>
              )}
            </div>
            {availableBranches.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">Select a region first.</p>
            ) : (
              <div className="space-y-2.5">
                {availableBranches.map(branch => (
                  <label key={branch} className="flex items-center gap-2.5 cursor-pointer group">
                    <Checkbox
                      checked={selectedBranches.includes(branch)}
                      onCheckedChange={() => toggleBranch(branch)}
                    />
                    <span className="text-sm group-hover:text-foreground transition-colors">
                      {branch}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <Separator />

          {/* Divisions */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Divisions</Label>
              {availableDivisions.length > 0 && (
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <Checkbox
                    checked={selectedDivisions.length === availableDivisions.length}
                    onCheckedChange={v => setSelectedDivisions(v ? [...availableDivisions] : [])}
                  />
                  <span className="text-xs text-muted-foreground">Select all</span>
                </label>
              )}
            </div>
            {availableDivisions.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">Select at least one branch first.</p>
            ) : (
              <div className="space-y-2.5">
                {availableDivisions.map(division => (
                  <label key={division} className="flex items-center gap-2.5 cursor-pointer group">
                    <Checkbox
                      checked={selectedDivisions.includes(division)}
                      onCheckedChange={() => toggleDivision(division)}
                    />
                    <span className="text-sm group-hover:text-foreground transition-colors">
                      {division}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <Separator />

          <label className="flex items-start gap-2.5 cursor-pointer group">
            <Checkbox
              checked={populateFromPrevious}
              onCheckedChange={v => setPopulateFromPrevious(v === true)}
              className="mt-0.5"
            />
            <div>
              <span className="text-sm group-hover:text-foreground transition-colors">
                Populate from previous month
              </span>
              <p className="text-xs text-muted-foreground mt-0.5">
                Pre-fill commentary from this region's most recent published or distributed
                report before the month above. Visuals are always freshly fetched.
              </p>
            </div>
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onOpenChange(false); }}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            <CalendarDays className="w-4 h-4 mr-2" />
            Create Report
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
